import React, { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { useDisplayType } from '../../../hooks/common';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { getInitialActiveTab, getInitialArticleCount } from '../RecentlyPublished.logic';
import { CompanyRecentlyPublishedContextType } from './CompanyRecentlyPublishedContext.types';

const RecentlyPublishedContext = createContext<CompanyRecentlyPublishedContextType | null>(null);

const RecentlyPublishedContextProvider = ({ children }: PropsWithChildren): React.ReactElement => {
  const { data: userPermissions } = useFetchUserPermissions();
  const [articleTypeFilter, setArticleTypeFilter] = useState(getInitialActiveTab(userPermissions!.userSubscription));
  const [articleCount, setArticleCount] = useState(getInitialArticleCount(userPermissions!.userSubscription));
  const [displayType, setDisplayType] = useDisplayType();

  const value: CompanyRecentlyPublishedContextType = useMemo(
    () => ({
      articleTypeFilter,
      onArticleTypeFilterChange: setArticleTypeFilter,
      articleCount,
      onArticleCountChange: setArticleCount,
      displayType,
      setDisplayType,
    }),
    [articleTypeFilter, setArticleTypeFilter, articleCount, setArticleCount, displayType, setDisplayType]
  );

  return <RecentlyPublishedContext.Provider value={value}>{children}</RecentlyPublishedContext.Provider>;
};

const useRecentlyPublishedContext = (): CompanyRecentlyPublishedContextType => {
  const context = useContext(RecentlyPublishedContext);
  if (!context) {
    throw new Error('Hook must be used within the provider.');
  }

  return context;
};

export { RecentlyPublishedContextProvider, useRecentlyPublishedContext };
